

// [XFORM] timing on setDataArrays.
//
// This function can now be called MORE THAN ONCE per login — once with the
// table the visible tab needs, then again with the rest. So it must never
// assume the payload it receives is the whole picture. Two consequences:
// it does not prune colours, and the colour allocator is fed from
// seriesKeyMap rather than from this payload.

import {setBothControls} from '../slices/controls/actions';
import {setBothRules} from '../slices/rules/actions';
import {setOta} from '../slices/otas';
import {setColors, addColors, normalizeColors} from '../slices/colors';
import {setAliases} from '../slices/aliases';
import {setBothSettings} from '../slices/settings/actions';

import hooks from './hooks';

import {setData, setLocations, updateSeriesKeyMap} from '../slices/data';
import {normalizeChartName} from '../slices/data/chartName';
import store from '../slices/store';
import {setDevices, setAllMeta} from '../slices/devices';
import {allocateColors} from '../slices/colors/allocate';

const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
const fmt   = (n) => n.toFixed(1).padStart(8) + ' ms';

export const setConfig = (config, dispatch) => {
    const { rules, controls, ota, settings, colors, meta, aliases } = config;
	dispatch(setBothControls(controls || []));
	dispatch(setBothRules(rules || []));
	dispatch(setOta(ota || []));
    dispatch(setColors(colors || {}));
    dispatch(setAliases(aliases || {}));
	dispatch(setBothSettings(settings || {}));
    dispatch(setAllMeta(meta || {}));
}


export const setDataArrays = (data, existingColors, token, dispatch) => {

    const tTotal = nowMs();

    // ── what did we actually get? ────────────────────────────────────────
    let totalDocs = 0;
    const shape = [];
    for (const k in data) {
        const n = (data[k] || []).length;
        totalDocs += n;
        shape.push(k.replace('table', 't').replace(/_.*/, '') + ':' + n);
    }
    console.log('[XFORM] setDataArrays  ' + totalDocs + ' docs  [' + shape.join(' ') + ']');

    const tNorm = nowMs();
    const rawColors = existingColors || {};
    existingColors = normalizeColors(rawColors);
    const repairedLegacy = JSON.stringify(rawColors) !== JSON.stringify(existingColors);
    console.log('[XFORM]   normalizeColors     ' + fmt(nowMs() - tNorm) +
                (repairedLegacy ? '   <-- legacy colors repaired (forces a PUT below)' : ''));

    const flatData = {};
    const keyMap = {};

    // ── PASS 1: the transpose ────────────────────────────────────────────
    const tPass1 = nowMs();
    let iterations = 0;
    let points = 0;

    for (const tableKey in data) {
        flatData[tableKey] = {};
        data[tableKey].forEach(function(elem) {
            const { timestamp, data: values } = elem;
            for (const deviceID in values) {
                for (const type in values[deviceID]) {
                    for (const rawChart in values[deviceID][type]) {
                        iterations++;
                        const chartName = normalizeChartName(rawChart);
                        if (!flatData[tableKey][chartName]) flatData[tableKey][chartName] = {};
                        const fields = values[deviceID][type][rawChart];
                        for (const fieldName in fields) {
                            points++;
                            const seriesKey = deviceID + '_' + fieldName;
                            keyMap[seriesKey] = { deviceID, fieldName };
                            if (!flatData[tableKey][chartName][seriesKey]) flatData[tableKey][chartName][seriesKey] = [];
                            flatData[tableKey][chartName][seriesKey].push({ time: timestamp, data: fields[fieldName] });
                        }
                    }
                }
            }
        });
    }
    const pass1 = nowMs() - tPass1;
    console.log('[XFORM]   PASS 1 transpose    ' + fmt(pass1) +
                '   ' + points.toLocaleString() + ' points, ' +
                iterations.toLocaleString() + ' chart-visits, ' +
                Object.keys(keyMap).length + ' series');
    console.log('[XFORM]   ^^ ' + (points / Math.max(pass1, 0.001)).toFixed(0) + ' points/ms. ' +
                'normalizeChartName is called ' + iterations.toLocaleString() +
                ' times — once per document per chart, for a value that only has a ' +
                'handful of distinct inputs. A Map cache there is nearly free.');

    // ── PASS 2: color allocation ─────────────────────────────────────────
    // The allocator must see EVERY series we know about, not just the ones in
    // this payload. It reconstructs a device's base hue by finding whichever of
    // its series holds shade 0 — hand it a partial list and it won't find the
    // anchor, so a device that already has a blue gets handed a second colour.
    // seriesKeyMap is the accumulated memory of every series ever seen, so we
    // start from that and layer this payload's keys on top. Same approach
    // processData and clearStyle already use.
    const tPass2 = nowMs();
    const storeKeyMap = store.getState().data.seriesKeyMap || {};
    const orderedKeyMap = {};
    for (const k in existingColors) if (storeKeyMap[k]) orderedKeyMap[k] = storeKeyMap[k];
    for (const k in keyMap)         if (!orderedKeyMap[k]) orderedKeyMap[k] = keyMap[k];

    const newColorEntries = allocateColors(orderedKeyMap, existingColors);
    const newColors = { ...existingColors, ...newColorEntries };
    console.log('[XFORM]   PASS 2 colors       ' + fmt(nowMs() - tPass2) +
                '   ' + Object.keys(newColorEntries).length + ' newly allocated');

    const oldKeys = Object.keys(existingColors).sort().join(',');
    const newKeys = Object.keys(newColors).sort().join(',');

    logFlatData(flatData);

    // ── dispatch: redux + immer have to walk this whole structure ────────
    const tDispatch = nowMs();
    dispatch(setColors(newColors));
    dispatch(setData(flatData));
    dispatch(updateSeriesKeyMap(keyMap));
    console.log('[XFORM]   dispatches          ' + fmt(nowMs() - tDispatch) +
                '   <-- redux-toolkit freezes state in dev; if this is huge, that is why');

	if (repairedLegacy || oldKeys !== newKeys) {
        // A PUT here on a NORMAL boot means the color set changed. If you see
        // this every time you load the page with no new devices, something is
        // producing unstable series keys and you are doing a pointless write
        // on every login.
        console.warn('[XFORM]   !! writing colors back to the server' +
                     (repairedLegacy ? ' (legacy repair)' : ' (key set changed)'));
        console.warn('[XFORM]      old: ' + Object.keys(existingColors).length + ' keys, ' +
                     'new: ' + Object.keys(newColors).length + ' keys');
        hooks.put('/update/colors', newColors, token);
    }

    console.log('[XFORM] setDataArrays TOTAL ' + fmt(nowMs() - tTotal) + '\n');
}


export const setLocationArrays = (locations, token, dispatch) => {
    if (!locations || !Object.keys(locations).length) {
        dispatch(setLocations(locations || {}));
        return;
    }

    const normalized = {};
    for (const rawChart in locations) {
        const chartName = normalizeChartName(rawChart);
        normalized[chartName] = Object.assign(normalized[chartName] || {}, locations[rawChart]);
    }
    locations = normalized;

    dispatch(setLocations(locations));

    const existingColors = store.getState().colors.allocated || {};
    const seriesKeyMap = store.getState().data.seriesKeyMap || {};

    const orderedKeyMap = {};
    for (const seriesKey in existingColors) {
        if (seriesKeyMap[seriesKey]) orderedKeyMap[seriesKey] = seriesKeyMap[seriesKey];
    }

    const keyMap = {};
    for (const chartName in locations) {
        for (const person in locations[chartName]) {
            const seriesKey = person + '_loc';
            keyMap[seriesKey] = { deviceID: person, fieldName: 'loc' };
            if (!orderedKeyMap[seriesKey]) orderedKeyMap[seriesKey] = keyMap[seriesKey];
        }
    }

    const newColorEntries = allocateColors(orderedKeyMap, existingColors);
    if (Object.keys(newColorEntries).length) {
        console.warn('[XFORM]   !! second colors PUT, from setLocationArrays');
        dispatch(addColors(newColorEntries));
        hooks.put('/update/colors', store.getState().colors.allocated, token);
    }

    dispatch(updateSeriesKeyMap(keyMap));
};


export const setTwins = (twins, dispatch) => {
	// NOTE: one dispatch per device. Each one runs every subscribed selector.
	// Fine at 5 devices, measurable at 100 — batch into a single setDevicesBulk
	// if the [BOOT] setTwins line ever gets loud.
	for (var deviceID in twins) {
		dispatch(setDevices({deviceID, twin: twins[deviceID]}));
	}
}


const logFlatData = (flatData) => {
    // console.log("---- flatData ----");
    // console.log(JSON.stringify(flatData, null, 2));
};